import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputLabel
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import useApi from 'hooks/useApi';
import DeleteField from '../../otherComponent/DeleteStep';
import DynamicFieldSettings from './shared/DynamicFieldSettings';
import Reportform from '../../Reportform';

interface ResourceComponentProps {
  values: any;
  index: number;
  setFieldValue: (field: string, value: any) => void;
  onSubmit?: (data: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  clientUID?: string;
  selectedNodeData: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function ResourceComponent({
  values,
  index,
  setFieldValue,
  onSubmit,
  handleChange,
  handleBlur,
  clientUID,
  selectedNodeData,
  onDeleteField,
  toSentenceCase,
  fieldTypeSelector,
  isSettingsOpen,
  onSettingsOpen,
  onSettingsClose
}: ResourceComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resourceTypes, setResourceTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingResourceTypes, setLoadingResourceTypes] = useState(false);
  const [hasLoadedResourceTypes, setHasLoadedResourceTypes] = useState(false);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  const { getResourceTypes } = useApi();

  const field = values.fields[index];
  const settings = field.settings || {};

  // Get multiple selection setting
  const isMultiple = settings.multiple || false;

  // Assignee (transaction ownership) settings
  const isAssignee = settings.isAssignee || false;
  const assigneeScope = settings.assigneeScope || 'RESOURCE_DATA';

  // // Unique code logic
  // const isThisFieldUnique = settings.isUniqueCode;
  // const otherUniqueExists = values.fields.some((f: any, i: number) => f.settings?.isUniqueCode && i !== index);

  // Only load resource types once when component mounts, and only if we haven't loaded them before
  useEffect(() => {
    if (!hasLoadedResourceTypes) {
      loadResourceTypes();
    }
  }, [hasLoadedResourceTypes]);

  // Backward-compat: for legacy fields that only have `resourceType` (no `resourceList`),
  // seed `resourceList` with the primary so the Search column reflects what the field
  // historically searched. Also normalize the very old shape where the resource type
  // was nested under `settings.resourceType`. This makes the table show BOTH Primary
  // and Search checked on the same row for legacy fields, so they keep working
  // without the admin having to reconfigure anything.
  useEffect(() => {
    const legacyType: string | undefined = field.resourceType || field.settings?.resourceType;
    if (!legacyType) return;

    // 1) Lift `settings.resourceType` → top-level `resourceType` if needed
    if (!field.resourceType && field.settings?.resourceType) {
      setFieldValue(`fields.${index}.resourceType`, field.settings.resourceType);
    }
    // 2) Seed `resourceList` with the primary if it's empty/unset
    if (!field.resourceList || field.resourceList.length === 0) {
      setFieldValue(`fields.${index}.resourceList`, [legacyType]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
    // Only load resource types if we haven't loaded them yet
    if (!hasLoadedResourceTypes) {
      loadResourceTypes();
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const loadResourceTypes = async () => {
    if (hasLoadedResourceTypes) return; // Prevent multiple calls
    
    setLoadingResourceTypes(true);
    try {
      const response = await getResourceTypes(clientUID, null);
      if (response?.success && Array.isArray(response.data)) {
        setResourceTypes(response.data);
      } else if (Array.isArray(response)) {
        setResourceTypes(response);
      } else {
        console.error('Invalid resource types response:', response);
        setResourceTypes([]);
      }
      setHasLoadedResourceTypes(true); // Mark as loaded
    } catch (error) {
      console.error('Failed to load resource types:', error);
      setResourceTypes([]);
      setHasLoadedResourceTypes(true); // Mark as loaded even on error to prevent retries
    } finally {
      setLoadingResourceTypes(false);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-resource-popover' : undefined;

  // Get the resource type name for display
  const getResourceTypeName = () => {
    if (!field.resourceType) return 'No Resource Type';
    const resourceType = resourceTypes.find(rt => rt.id === field.resourceType);
    return resourceType?.name || `loading...`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(field.question || 'Untitled Resource Field')}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'primary.main', fontWeight: 'bold', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {getResourceTypeName()}
                {field.resourceList?.length > 1 ? ` +${field.resourceList.length - 1}` : ''}
              </Typography>
            </Box>

            <Box ref={settingsButtonRef} sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
              <SettingsIcon fontSize="medium" />
            </Box>
            <DeleteField 
              onDeleteField={() => onDeleteField?.(index)}
              selectedNodeData={selectedNodeData}
              fieldIndex={index}
              fieldData={values.fields[index]}
            />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
            <Typography variant="subtitle1">Resource Field Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name={`fields.${index}.question`}
                defaultValue={field.question}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name={`fields.${index}.description`}
                defaultValue={field.description}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel sx={{ mb: 1 }}>Resource Types</InputLabel>
              {loadingResourceTypes ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Loading...
                </Typography>
              ) : resourceTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No resource types available
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', px: 1 }}>Resource name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', px: 1 }} align="center">Search</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', px: 1 }} align="center">Primary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resourceTypes.map((type) => {
                      const inSearchList = (field.resourceList || []).includes(type.id);
                      const isPrimary = field.resourceType === type.id;
                      return (
                        <TableRow key={type.id}>
                          <TableCell sx={{ px: 1 }}>{type.name}</TableCell>
                          <TableCell sx={{ px: 1 }} align="center">
                            <Checkbox
                              size="small"
                              checked={inSearchList}
                              onChange={(e) => {
                                const list: string[] = field.resourceList || [];
                                const next = e.target.checked
                                  ? Array.from(new Set([...list, type.id]))
                                  : list.filter((id: string) => id !== type.id);
                                setFieldValue(`fields.${index}.resourceList`, next);
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ px: 1 }} align="center">
                            <Checkbox
                              size="small"
                              checked={isPrimary}
                              onChange={(e) => {
                                // Primary and Search are independent — Primary only controls the
                                // auto-update target on submission, NOT what's searchable.
                                if (e.target.checked) {
                                  setFieldValue(`fields.${index}.resourceType`, type.id);
                                } else {
                                  setFieldValue(`fields.${index}.resourceType`, '');
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.required || false}
                    onChange={(event) => setFieldValue(`fields.${index}.settings.required`, event.target.checked)}
                  />
                }
                label="Required"
              />
            </Grid>

            <Grid item xs={12} sx={{mt:-3}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isMultiple}
                    onChange={(event) => {
                      setFieldValue(`fields.${index}.settings.multiple`, event.target.checked);
                    }}
                  />
                }
                label="Multiple Selection"
              />
            </Grid>

            <Grid item xs={12} sx={{mt:-2}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAssignee}
                    onChange={(event) => {
                      setFieldValue(`fields.${index}.settings.isAssignee`, event.target.checked);
                      if (event.target.checked && !settings.assigneeScope) {
                        setFieldValue(`fields.${index}.settings.assigneeScope`, 'RESOURCE_DATA');
                      }
                    }}
                  />
                }
                label="Assign transaction to whoever is picked here"
              />
            </Grid>

            {isAssignee ? (
              <Grid item xs={12} sx={{ mt: -2, pl: 4 }}>
                <FormLabel sx={{ fontSize: '0.8rem' }}>Assign to</FormLabel>
                <RadioGroup
                  value={assigneeScope}
                  onChange={(event) => setFieldValue(`fields.${index}.settings.assigneeScope`, event.target.value)}
                >
                  <FormControlLabel
                    value="RESOURCE_DATA"
                    control={<Radio size="small" />}
                    label="The specific person picked at submission"
                  />
                  <FormControlLabel
                    value="RESOURCE_TYPE"
                    control={<Radio size="small" />}
                    label="Anyone with that person's role"
                  />
                </RadioGroup>
              </Grid>
            ) : null}

            <DynamicFieldSettings
              values={values}
              index={index}
              setFieldValue={setFieldValue}
              open={Boolean(anchorEl)}
              helperText="Reuse a resource picked in an earlier step. The source field must be the same resource type."
            />

            {/* Unique Code logic */}
            {/* <Grid item xs={12}>
              {!otherUniqueExists || isThisFieldUnique ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isThisFieldUnique}
                      onChange={(event) => {
                        const isChecked = event.target.checked;
                        setFieldValue(`fields.${index}.settings.isUniqueCode`, isChecked);
                        if (isChecked) {
                          setFieldValue(`fields.${index}.settings.required`, true);
                        }
                      }}
                    />
                  }
                  label="Unique Code"
                />
              ) : null}
            </Grid> */}

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.report?.enabled || false}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      setFieldValue(
                        `fields.${index}.settings.report`,
                        isChecked
                          ? { ...(settings.report || {}), enabled: true }
                          : { enabled: false, uid: null, name: null }
                      );
                    }}
                  />
                }
                label="Report "
              />
            </Grid>

            {settings.report?.enabled && (
              <Grid item xs={12} sx={{ mt: -2 }}>
                <Reportform name={`fields.${index}.settings.report`} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button type="submit" size="small" variant="contained" onClick={handleClosePopover}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}
